import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Cpu,
    Lightning,
    ShieldCheck,
    Waveform,
    ChartLineUp,
    Globe,
    Wallet,
    CheckCircle,
    Sparkle,
    CircuitryIcon,
    XLogoIcon,
    DiscordLogoIcon,
    ThermometerHot,
    Flag,
    Radio,
    ArrowsInLineHorizontal,
} from "@phosphor-icons/react";
import { api, fmtAone } from "@/lib/api";
import { useLivePrice } from "@/context/LivePriceContext";
import AegisLogo from "@/components/AegisLogo";
import WorldMap from "@/components/WorldMap";

export default function HomePage({ onConnect }) {
    return (
        <div className="min-h-screen">
            <Hero onConnect={onConnect} />
            <LiveFeedBanner />
            <WithdrawalTicker />
            <Mission />
            <AutoPool />
            <RevenueEngine />
            <HalvingCountdown />
            <HowItWorks />
            <MinerPreview />
            <NetworkStats />
            <Features />
            <TokenPreview />
            <UseCases />
            <Roadmap />
            <FinalCta onConnect={onConnect} />
            <Footer />
        </div>
    );
}

/* ---------------------------------------------------------------- HERO */
function Hero({ onConnect }) {
    return (
        <section className="relative overflow-hidden bg-hero-gradient">
            <div className="absolute inset-0 pointer-events-none opacity-70">
                <WorldMap />
            </div>
            <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent 20%, var(--bg) 100%)" }} />
            <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 pt-20 pb-28 sm:pt-28 sm:pb-40">
                <div className="max-w-4xl">
                    <div className="inline-flex items-center gap-2 chip chip-live mb-8" data-testid="home-live-chip">
                        Early Bird Program · Mining Live
                    </div>
                    <h1 className="text-[48px] sm:text-[80px] lg:text-[96px] font-extrabold leading-[0.95] tracking-[-0.05em] text-[var(--text)]">
                        Predict every<br />
                        <span className="gradient-text-blue">electrical failure</span><br />
                        before it happens.
                    </h1>
                    <p className="mt-8 text-[18px] sm:text-[22px] leading-[1.45] text-[var(--text-2)] max-w-2xl">
                        Aegis One is a global DePIN network of miner nodes contributing compute to real-time predictive-failure intelligence. Deposit USDT, buy hashrate, and earn AONE that auto-swaps back to USDT the moment you withdraw.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-3">
                        <button
                            data-testid="home-hero-cta-primary"
                            onClick={onConnect}
                            className="btn-primary !text-[15px] !py-4 !px-7"
                        >
                            Start Mining AONE <ArrowRight size={14} />
                        </button>
                        <Link
                            to="/miner"
                            data-testid="home-hero-cta-secondary"
                            className="btn-ghost !text-[15px] !py-4 !px-7"
                        >
                            See mining plans
                        </Link>
                    </div>

                    <div className="mt-20 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl">
                        <HeroStat label="Token" value="AONE" />
                        <HeroStat label="Supply" value="10 B" />
                        <HeroStat label="Launch" value="$0.0001" />
                        <HeroStat label="Chain" value="Aegis L1" />
                    </div>
                </div>
            </div>
        </section>
    );
}

function HeroStat({ label, value }) {
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--text-3)]">{label}</div>
            <div className="text-[24px] sm:text-[30px] font-bold text-[var(--text)] mt-1.5 tracking-[-0.02em] tabular-nums">
                {value}
            </div>
        </div>
    );
}

/* ---------------------------------------------------------------- LIVE FEED */
const FEED_TEMPLATES = [
    { icon: <ThermometerHot size={14} weight="duotone" />, kind: "Thermal drift", spot: "Grid substation" },
    { icon: <Waveform size={14} weight="duotone" />, kind: "Harmonic distortion", spot: "Industrial motor" },
    { icon: <Lightning size={14} weight="duotone" />, kind: "Insulation breakdown", spot: "Transformer coil" },
    { icon: <CircuitryIcon size={14} weight="duotone" />, kind: "Partial discharge", spot: "MV switchgear" },
    { icon: <Radio size={14} weight="duotone" />, kind: "Frequency anomaly", spot: "Solar inverter" },
    { icon: <Flag size={14} weight="duotone" />, kind: "Load imbalance", spot: "Data center PDU" },
    { icon: <Cpu size={14} weight="duotone" />, kind: "Bearing wear signature", spot: "Turbine bearing" },
];
const REGIONS = ["Mumbai · IN", "Frankfurt · DE", "Dubai · AE", "Singapore · SG", "São Paulo · BR", "Tokyo · JP", "Bengaluru · IN", "New York · US", "Riyadh · SA", "Amsterdam · NL", "Sydney · AU", "Stockholm · SE"];

function LiveFeedBanner() {
    const [items, setItems] = useState([]);
    const idRef = useRef(0);

    useEffect(() => {
        // Seed with 8 initial rows
        const seed = Array.from({ length: 8 }).map(() => generateEvent(idRef));
        setItems(seed);
        const interval = setInterval(() => {
            setItems((prev) => [generateEvent(idRef), ...prev.slice(0, 11)]);
        }, 2200);
        return () => clearInterval(interval);
    }, []);

    return (
        <section className="border-y" style={{ borderColor: "var(--border)", background: "var(--bg-surface)" }} data-testid="home-live-feed">
            <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-4 sm:py-5 flex items-center gap-4 overflow-hidden">
                <div className="shrink-0 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[var(--primary-glow)]" style={{ boxShadow: "0 0 0 3px rgba(0,229,255,0.25)" }} />
                    <span className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-[var(--primary-glow)] font-medium">
                        Live · Anomaly Feed
                    </span>
                </div>
                <div className="flex-1 overflow-hidden">
                    <div className="flex gap-3 sm:gap-5 whitespace-nowrap animate-in fade-in slide-in-from-right">
                        {items.map((it) => (
                            <div key={it.id} className="flex items-center gap-2 shrink-0 text-[12.5px]">
                                <span className="text-[var(--primary-glow)]">{it.icon}</span>
                                <span className="text-[var(--text)]">{it.kind}</span>
                                <span className="text-[var(--text-3)]">·</span>
                                <span className="text-[var(--text-2)]">{it.spot}</span>
                                <span className="text-[var(--text-3)]">·</span>
                                <span className="font-mono text-[var(--text-3)]">{it.region}</span>
                                <span className="text-[var(--text-4)]">·</span>
                                <span className="font-mono text-[var(--primary-glow)]">{it.confidence}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function generateEvent(idRef) {
    const tmpl = FEED_TEMPLATES[Math.floor(Math.random() * FEED_TEMPLATES.length)];
    return {
        id: ++idRef.current,
        icon: tmpl.icon,
        kind: tmpl.kind,
        spot: tmpl.spot,
        region: REGIONS[Math.floor(Math.random() * REGIONS.length)],
        confidence: Math.floor(84 + Math.random() * 15),
    };
}

/* ---------------------------------------------------------------- MISSION */
function Mission() {
    return (
        <section className="py-24 sm:py-32 px-5 sm:px-8" data-testid="home-mission">
            <div className="max-w-[1280px] mx-auto grid lg:grid-cols-2 gap-16 items-start">
                <div>
                    <div className="eyebrow mb-4">Our Mission</div>
                    <h2 className="text-[36px] sm:text-[56px] font-bold leading-[1.03] tracking-[-0.035em] text-[var(--text)]">
                        Global electrical intelligence, owned by everyone.
                    </h2>
                </div>
                <div className="text-[17px] leading-[1.65] text-[var(--text-2)] space-y-5 lg:pt-8">
                    <p>
                        Electricity powers everything. Yet the machines that generate, transmit and consume it operate in the dark - silently degrading until a spectacular failure takes down a factory, a hospital, a data center, an entire neighborhood.
                    </p>
                    <p>
                        Legacy monitoring reacts after the fact. Centralized AI providers hoard the data and their model outputs. Aegis One flips the model: <span className="text-[var(--text)] font-medium">a decentralized fleet of miner nodes contributes compute to a shared predictive-failure network, incentivized by AONE.</span>
                    </p>
                    <p>
                        Every miner is a sensor. Every node is a stakeholder. Every prediction is a receipt on the Aegis L1.
                    </p>
                </div>
            </div>
        </section>
    );
}

/* ---------------------------------------------------------------- WITHDRAW TICKER */
function WithdrawalTicker() {
    const [items, setItems] = useState([]);
    useEffect(() => {
        const load = () => api.get("/network/recent-withdrawals").then((r) => setItems(r.data.withdrawals || [])).catch(() => {});
        load();
        const id = setInterval(load, 20000);
        return () => clearInterval(id);
    }, []);
    if (!items.length) return null;
    const doubled = [...items, ...items];
    return (
        <section className="border-b overflow-hidden" style={{ borderColor: "var(--border)", background: "var(--bg-raised)" }} data-testid="home-withdraw-ticker">
            <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-3 flex items-center gap-4">
                <div className="shrink-0 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[var(--success)]" style={{ boxShadow: "0 0 0 3px rgba(16,185,129,0.25)" }} />
                    <span className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-[var(--success)] font-medium">
                        Recent Withdrawals
                    </span>
                </div>
                <div className="flex-1 overflow-hidden">
                    <div className="flex gap-8 whitespace-nowrap ticker-marquee">
                        {doubled.map((tx, i) => (
                            <div key={`${tx.hash}-${i}`} className="flex items-center gap-3 shrink-0 text-[12.5px]">
                                <span className="font-mono text-[var(--text-3)]">{tx.to_masked}</span>
                                <span className="text-[var(--text-2)]">received</span>
                                <span className="font-mono text-[var(--success)]">${tx.amount.toFixed(4)} USDT</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ---------------------------------------------------------------- HALVING COUNTDOWN */
function HalvingCountdown() {
    const [halving, setHalving] = useState(null);
    useEffect(() => {
        api.get("/network/halving").then((r) => setHalving(r.data)).catch(() => {});
    }, []);
    if (!halving) return null;
    return (
        <section className="py-16 px-5 sm:px-8 border-t" style={{ borderColor: "var(--border)", background: "var(--bg-surface)" }} data-testid="home-halving">
            <div className="max-w-[1280px] mx-auto card p-8 sm:p-10 flex flex-wrap gap-8 items-center justify-between">
                <div className="max-w-xl">
                    <div className="eyebrow mb-3">Next Halving</div>
                    <h2 className="text-[28px] sm:text-[36px] font-bold text-[var(--text)] tracking-[-0.03em]">
                        Early miners earn more.
                    </h2>
                    <p className="mt-3 text-[14.5px] text-[var(--text-2)] leading-[1.6]">
                        Global emission halves from {halving.current_daily_emission.toLocaleString()} to {halving.next_daily_emission.toLocaleString()} AONE per day. Every plan bought before halving locks in the current rate for the full 365-day contract.
                    </p>
                </div>
                <div className="text-right">
                    <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)]">Halves in</div>
                    <div className="text-[52px] sm:text-[64px] font-extrabold tabular-nums font-mono text-[var(--primary-glow)] leading-none">
                        {Math.max(0, halving.next_halving_days)}
                    </div>
                    <div className="text-[13px] text-[var(--text-3)] mt-1">days</div>
                </div>
            </div>
        </section>
    );
}

/* ---------------------------------------------------------------- AUTO POOL NARRATIVE */
function AutoPool() {
    const { price, changePct } = useLivePrice();
    const up = changePct >= 0;

    return (
        <section className="py-24 px-5 sm:px-8 border-t" style={{ borderColor: "var(--border)" }} data-testid="home-autopool">
            <div className="max-w-[1280px] mx-auto grid lg:grid-cols-2 gap-14 items-start">
                <div>
                    <div className="eyebrow mb-4">Auto-Pool Treasury</div>
                    <h2 className="text-[36px] sm:text-[52px] font-bold leading-[1.03] tracking-[-0.035em] text-[var(--text)]">
                        Mined AONE, paid as USDT.
                    </h2>
                    <p className="mt-5 text-[17px] leading-[1.65] text-[var(--text-2)] max-w-lg">
                        Your mining rewards are quoted in AONE, but every claim is <span className="text-[var(--text)] font-medium">auto-swapped to USDT</span> at the live network rate. USDT lands in your Aegis wallet the same second - withdraw to any external wallet on Tron, BSC, Ethereum or Polygon.
                    </p>
                    <div className="mt-6 space-y-3 text-[14px] text-[var(--text-2)]">
                        <Bullet>Live AONE/USDT rate updated every 100ms across the global network.</Bullet>
                        <Bullet>50/50 Auto-Pool split: half of every deposit is locked as platform reserve, half funds miner payouts.</Bullet>
                        <Bullet>Reserve is never distributable - the payout pool can never drain below zero.</Bullet>
                        <Bullet>Per-claim cap set to 2 percent of the payout pool, protecting every miner.</Bullet>
                    </div>
                </div>
                <div className="card p-7 sm:p-9">
                    <div className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-[var(--text-3)] mb-6">
                        Live Market Snapshot
                    </div>
                    <div className="space-y-4">
                        <TreasuryRow
                            label="AONE / USDT"
                            value={`$${price.toFixed(6)}`}
                            sub={`${up ? "+" : ""}${changePct.toFixed(2)}%`}
                            positive={up}
                            live
                        />
                        <TreasuryRow label="Update interval" value="100 ms" />
                        <TreasuryRow label="Volatility band" value="±2.0 %" />
                        <TreasuryRow label="Auto-Pool status" value="Healthy" positive live />
                        <TreasuryRow label="Reserve split" value="50 / 50" />
                        <TreasuryRow label="Mainnet" value="Aegis L1" />
                    </div>
                    <p className="mt-6 text-[11.5px] text-[var(--text-3)] leading-relaxed">
                        Treasury balances are held in on-chain multisigs and are not publicly enumerated to protect the network from front-running. Auto-Pool solvency is displayed as a live health indicator.
                    </p>
                </div>
            </div>
        </section>
    );
}

/* ---------------------------------------------------------------- REVENUE ENGINE (NEW NARRATIVE) */
function RevenueEngine() {
    return (
        <section className="py-24 px-5 sm:px-8 border-t" style={{ borderColor: "var(--border)", background: "var(--bg-surface)" }} data-testid="home-revenue">
            <div className="max-w-[1280px] mx-auto">
                <div className="mb-14 max-w-3xl">
                    <div className="eyebrow mb-4">How the network generates USDT</div>
                    <h2 className="text-[36px] sm:text-[52px] font-bold leading-[1.03] tracking-[-0.035em] text-[var(--text)]">
                        Real revenue backs every miner payout.
                    </h2>
                    <p className="mt-6 text-[16.5px] leading-[1.65] text-[var(--text-2)]">
                        The Auto-Pool is not a bag of tokens waiting to be dumped. Every USDT that leaves the treasury toward a miner is offset by USDT that entered from four continuously growing revenue streams. That is why the payout pool refills itself and why AONE has real, underlying demand.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <RevCard
                        no="01"
                        title="Miner terminal deposits"
                        body="Every $25, $100 and $500 hashrate plan flows into the Auto-Pool. 50 percent stays locked as reserve, 50 percent funds live miner claims. Network scales linearly with participation."
                    />
                    <RevCard
                        no="02"
                        title="B2B predictive-failure API"
                        body="Utilities, data centers, factories and renewables subscribe in USDT to consume the anomaly stream generated by miner compute. Enterprise revenue is a continuous inflow to the treasury."
                    />
                    <RevCard
                        no="03"
                        title="Cross-chain bridge fees"
                        body="0.5 percent settlement fee on every external USDT withdrawal covers on-chain gas and captures value from network activity. Fees are recycled straight into the payout pool."
                    />
                    <RevCard
                        no="04"
                        title="Validator + governance fees"
                        body="Aegis L1 charges gas on every native transaction. A share flows to the Auto-Pool, tightening the loop between chain activity and miner rewards."
                    />
                </div>

                <div className="mt-14 grid md:grid-cols-2 gap-4">
                    <div className="card p-7 sm:p-9">
                        <div className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-[var(--primary-glow)] mb-3">Why the price moves</div>
                        <div className="text-[19px] font-semibold text-[var(--text)]">AONE reflects real network demand, in real time.</div>
                        <p className="mt-3 text-[14.5px] leading-[1.65] text-[var(--text-2)]">
                            AONE mints continuously against the fixed 6B mining pool. Every second, miners globally claim rewards - which the Auto-Pool auto-swaps at the live rate. That live rate is priced against two forces: the compute capacity coming online (supply-side, from new hashrate purchases) and the USDT inflow from B2B subscriptions plus bridge activity (demand-side).
                        </p>
                        <p className="mt-3 text-[14.5px] leading-[1.65] text-[var(--text-2)]">
                            The ticker moves every 100 ms because the network is settling globally, all the time. No frozen quotes. No stale pools. Just a running heartbeat of an active DePIN economy.
                        </p>
                    </div>
                    <div className="card p-7 sm:p-9">
                        <div className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-[var(--primary-glow)] mb-3">Why the platform cannot collapse</div>
                        <div className="text-[19px] font-semibold text-[var(--text)]">Reserve-backed, not promise-backed.</div>
                        <p className="mt-3 text-[14.5px] leading-[1.65] text-[var(--text-2)]">
                            Half of every deposit is permanently locked as platform reserve. Only the remaining half can ever be distributed. That means even if 100 percent of miners tried to claim simultaneously, they can only ever pull from the distributable half - the reserve stays intact.
                        </p>
                        <p className="mt-3 text-[14.5px] leading-[1.65] text-[var(--text-2)]">
                            Combined with per-claim throttles, halving-driven emission decay, and continuous revenue inflows from the B2B API, the Auto-Pool is designed to run in perpetuity.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

function RevCard({ no, title, body }) {
    return (
        <div className="card panel-hover p-7">
            <div className="font-mono text-[13px] text-[var(--primary-glow)]">{no}</div>
            <div className="text-[16.5px] font-semibold text-[var(--text)] mt-3">{title}</div>
            <p className="text-[13px] text-[var(--text-2)] leading-[1.6] mt-2">{body}</p>
        </div>
    );
}

function Bullet({ children }) {
    return (
        <div className="flex items-start gap-2.5">
            <CheckCircle size={15} weight="fill" className="text-[var(--primary-glow)] shrink-0 mt-0.5" />
            <span>{children}</span>
        </div>
    );
}

function TreasuryRow({ label, value, sub, positive, live }) {
    return (
        <div className="flex items-center justify-between border-b pb-3" style={{ borderColor: "var(--border)" }}>
            <div className="text-[13.5px] text-[var(--text-3)] flex items-center gap-2">
                {label}
                {live && <span className="chip chip-live !py-0 !text-[9px]">LIVE</span>}
            </div>
            <div className="text-right">
                <div className={`font-mono text-[16px] font-semibold tabular-nums ${positive === false ? "text-[var(--danger)]" : positive ? "text-[var(--success)]" : "text-[var(--text)]"}`}>
                    {value}
                </div>
                {sub && <div className={`font-mono text-[11px] ${positive === false ? "text-[var(--danger)]" : "text-[var(--success)]"}`}>{sub}</div>}
            </div>
        </div>
    );
}

/* ---------------------------------------------------------------- HOW IT WORKS */
function HowItWorks() {
    const steps = [
        { n: "01", t: "Deposit USDT", d: "Fund your wallet from any chain - BEP-20, ERC-20, TRC-20, Polygon or Solana. Cross-chain deposits credit the same balance." },
        { n: "02", t: "Choose a plan", d: "$25 Starter, $100 Pro or $500 Elite. Each plan buys a fixed slice of the Aegis network hashrate." },
        { n: "03", t: "Contribute compute", d: "Your hashrate joins the DePIN fleet. Nodes run federated inference against real electrical telemetry from partner grids and sites." },
        { n: "04", t: "Earn AONE", d: "Rewards accrue every second. Transfer to your Aegis wallet anytime - swap, hold or send." },
    ];
    return (
        <section className="py-24 px-5 sm:px-8 border-t" style={{ borderColor: "var(--border)" }} data-testid="home-how">
            <div className="max-w-[1280px] mx-auto">
                <div className="mb-14 max-w-3xl">
                    <div className="eyebrow mb-4">How It Works</div>
                    <h2 className="text-[36px] sm:text-[52px] font-bold leading-[1.03] tracking-[-0.035em] text-[var(--text)]">
                        From wallet deposit to AONE in under a minute.
                    </h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {steps.map((s) => (
                        <div key={s.n} className="card p-7 relative overflow-hidden">
                            <div className="font-mono text-[13px] text-[var(--primary-glow)] mb-4">{s.n}</div>
                            <div className="text-[18px] font-semibold text-[var(--text)]">{s.t}</div>
                            <p className="text-[14px] text-[var(--text-2)] leading-[1.55] mt-2">{s.d}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------------------------------------------------------------- MINER PREVIEW */
function MinerPreview() {
    const plans = [
        { name: "Starter", price: 25, hash: "1 GH/s", daily: "500 AONE/day" },
        { name: "Pro", price: 100, hash: "5 GH/s", daily: "2,500 AONE/day", featured: true },
        { name: "Elite", price: 500, hash: "30 GH/s", daily: "15,000 AONE/day" },
    ];
    return (
        <section className="py-24 px-5 sm:px-8 border-t" style={{ borderColor: "var(--border)", background: "var(--bg-surface)" }} data-testid="home-miner">
            <div className="max-w-[1280px] mx-auto">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-14">
                    <div className="max-w-2xl">
                        <div className="eyebrow mb-4">Mining Plans</div>
                        <h2 className="text-[36px] sm:text-[52px] font-bold leading-[1.03] tracking-[-0.035em] text-[var(--text)]">
                            Pick a plan. Start earning today.
                        </h2>
                    </div>
                    <Link to="/miner" className="btn-primary !text-[14px]" data-testid="home-miner-cta">
                        Go to Miner Terminal <ArrowRight size={14} />
                    </Link>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                    {plans.map((p) => (
                        <div
                            key={p.name}
                            className={`card p-7 relative ${p.featured ? "border-2 glow-blue" : ""}`}
                            style={p.featured ? { borderColor: "var(--primary)" } : {}}
                        >
                            {p.featured && (
                                <div className="absolute -top-3 left-6 chip chip-live text-[10px] !py-0.5">
                                    Most Popular
                                </div>
                            )}
                            <div className="text-[13px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)]">{p.name}</div>
                            <div className="mt-4 flex items-baseline gap-1">
                                <span className="text-[44px] font-bold text-[var(--text)]">${p.price}</span>
                                <span className="text-[13px] text-[var(--text-3)]">USDT</span>
                            </div>
                            <div className="mt-6 space-y-2.5 text-[13.5px]">
                                <PlanBullet>{p.hash} hashrate</PlanBullet>
                                <PlanBullet>~{p.daily}</PlanBullet>
                                <PlanBullet>365-day mining contract</PlanBullet>
                                <PlanBullet>Instant claims · zero fees</PlanBullet>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function PlanBullet({ children }) {
    return (
        <div className="flex items-center gap-2 text-[var(--text-2)]">
            <CheckCircle size={14} weight="fill" className="text-[var(--primary-glow)] shrink-0" />
            <span>{children}</span>
        </div>
    );
}

/* ---------------------------------------------------------------- NETWORK STATS */
function NetworkStats() {
    const [net, setNet] = useState(null);
    useEffect(() => {
        api.get("/miner/network").then((r) => setNet(r.data)).catch(() => {});
        const id = setInterval(() => {
            api.get("/miner/network").then((r) => setNet(r.data)).catch(() => {});
        }, 15000);
        return () => clearInterval(id);
    }, []);

    return (
        <section className="py-24 px-5 sm:px-8 border-t" style={{ borderColor: "var(--border)" }} data-testid="home-network">
            <div className="max-w-[1280px] mx-auto">
                <div className="mb-14 max-w-2xl">
                    <div className="eyebrow mb-4">The Network</div>
                    <h2 className="text-[36px] sm:text-[52px] font-bold leading-[1.03] tracking-[-0.035em] text-[var(--text)]">
                        Live now. Growing every block.
                    </h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <BigStat label="Network Hashrate" value={net ? `${net.total_hashrate_ghs.toFixed(1)}` : "-"} unit="GH/s" />
                    <BigStat label="Active Miners" value={net ? net.active_miners.toLocaleString() : "-"} unit="nodes" />
                    <BigStat label="AONE Mined" value={net ? fmtAone(net.mining_pool_total - net.mining_pool_remaining, 0) : "-"} unit={`of ${fmtAone(6_000_000_000, 0)}`} />
                    <BigStat label="Launch Price" value={net ? `$${net.launch_price_usdt}` : "-"} unit="per AONE" />
                </div>
            </div>
        </section>
    );
}

function BigStat({ label, value, unit }) {
    return (
        <div className="card p-6">
            <div className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-[var(--text-3)]">{label}</div>
            <div className="text-[34px] sm:text-[42px] font-bold text-[var(--text)] tabular-nums mt-2 font-mono">{value}</div>
            <div className="text-[11.5px] font-mono text-[var(--text-3)] mt-1">{unit}</div>
        </div>
    );
}

/* ---------------------------------------------------------------- FEATURES */
function Features() {
    const features = [
        { icon: <Waveform size={22} weight="duotone" />, title: "Predictive Failure AI", body: "Federated models trained on harmonic distortion, thermal drift, partial discharge, insulation breakdown, bearing signatures and load imbalance." },
        { icon: <Globe size={22} weight="duotone" />, title: "Any-chain USDT deposit", body: "Send USDT from BEP-20, ERC-20, TRC-20, Polygon or Solana. One balance. One wallet. The Aegis Network bridge routes unusual chains automatically." },
        { icon: <Cpu size={22} weight="duotone" />, title: "Hashrate-based rewards", body: "Every plan buys a slice of network hashrate. AONE emits continuously against your share of the 6B mining pool." },
        { icon: <ShieldCheck size={22} weight="duotone" />, title: "Enterprise validators", body: "Global validator set proposes blocks, secures the Aegis L1, and settles predictive-failure receipts with sub-2 s finality." },
        { icon: <ChartLineUp size={22} weight="duotone" />, title: "Real-time earnings", body: "Live pending AONE ticker on your miner dashboard. Transfer to wallet anytime, no lockups, no vesting." },
        { icon: <Sparkle size={22} weight="duotone" />, title: "Zero hardware", body: "No rigs, no power bills, no configuration. Cloud-provisioned nodes are online the moment your deposit clears." },
    ];
    return (
        <section className="py-24 px-5 sm:px-8 border-t" style={{ borderColor: "var(--border)", background: "var(--bg-surface)" }} data-testid="home-features">
            <div className="max-w-[1280px] mx-auto">
                <div className="mb-14 max-w-2xl">
                    <div className="eyebrow mb-4">The Product</div>
                    <h2 className="text-[36px] sm:text-[52px] font-bold leading-[1.03] tracking-[-0.035em] text-[var(--text)]">
                        Every miner is a sensor. Every block secures the grid.
                    </h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {features.map((f) => (
                        <div key={f.title} className="card panel-hover p-7">
                            <div className="product-tile-icon mb-5">{f.icon}</div>
                            <div className="text-[17px] font-semibold text-[var(--text)]">{f.title}</div>
                            <p className="text-[14px] text-[var(--text-2)] leading-[1.55] mt-2">{f.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------------------------------------------------------------- TOKEN PREVIEW */
function TokenPreview() {
    const alloc = [
        { key: "Mining Rewards", pct: 60, tone: "#2563EB" },
        { key: "Team", pct: 10, tone: "#3B82F6" },
        { key: "Ecosystem", pct: 10, tone: "#60A5FA" },
        { key: "Validators", pct: 10, tone: "#93C5FD" },
        { key: "Public Sale", pct: 10, tone: "#00E5FF" },
    ];
    return (
        <section className="py-24 px-5 sm:px-8 border-t" style={{ borderColor: "var(--border)" }} data-testid="home-tokenomics">
            <div className="max-w-[1280px] mx-auto grid lg:grid-cols-2 gap-14 items-start">
                <div>
                    <div className="eyebrow mb-4">Tokenomics</div>
                    <h2 className="text-[36px] sm:text-[52px] font-bold leading-[1.03] tracking-[-0.035em] text-[var(--text)]">
                        10 billion AONE.<br />Fair from day one.
                    </h2>
                    <p className="mt-5 text-[16px] leading-[1.6] text-[var(--text-2)] max-w-lg">
                        60% of total supply is reserved for miners. No pre-mine to insiders. No VC lockups. No hidden allocations. Fair-launched at $0.0001 for global access.
                    </p>
                    <Link to="/tokenomics" className="link-accent mt-8 inline-flex text-[15px]" data-testid="home-tokenomics-link">
                        Full tokenomics <ArrowRight size={13} />
                    </Link>
                </div>
                <div className="card p-7 sm:p-9">
                    <div className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-[var(--text-3)] mb-6">
                        Supply distribution
                    </div>
                    <div className="flex items-center rounded-md overflow-hidden h-4 mb-8" style={{ background: "var(--bg-raised)" }}>
                        {alloc.map((a) => (
                            <div key={a.key} style={{ width: `${a.pct}%`, background: a.tone }} />
                        ))}
                    </div>
                    <div className="space-y-3">
                        {alloc.map((a) => (
                            <div key={a.key} className="flex items-center justify-between text-[14px]">
                                <div className="flex items-center gap-3">
                                    <span className="w-3 h-3 rounded-sm" style={{ background: a.tone }} />
                                    <span className="text-[var(--text)] font-medium">{a.key}</span>
                                </div>
                                <span className="font-mono text-[var(--text-2)] tabular-nums">{a.pct}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ---------------------------------------------------------------- USE CASES */
function UseCases() {
    const cases = [
        { title: "Utilities & Grids", body: "Utilities anchor asset health signals from every transformer, breaker and feeder. Aegis surfaces failure risk 6–72 hours ahead of catastrophic events.", tag: "Utilities" },
        { title: "Data Centers", body: "PDU load imbalance, UPS battery degradation and cooling telemetry become predictive receipts on the Aegis L1 - auditable, portable across operators.", tag: "Data Centers" },
        { title: "Industrial & Manufacturing", body: "Motor bearing signatures, drive-frequency drift and insulation breakdown detected before a line goes down. Fewer stops, higher OEE.", tag: "Industrial" },
        { title: "Renewables", body: "Solar inverters and battery banks stream health data to the network. Aegis learns per-asset degradation and warns operators before yield drops.", tag: "Renewables" },
    ];
    return (
        <section className="py-24 px-5 sm:px-8 border-t" style={{ borderColor: "var(--border)", background: "var(--bg-surface)" }} data-testid="home-usecases">
            <div className="max-w-[1280px] mx-auto">
                <div className="mb-14 max-w-2xl">
                    <div className="eyebrow mb-4">Use Cases</div>
                    <h2 className="text-[36px] sm:text-[52px] font-bold leading-[1.03] tracking-[-0.035em] text-[var(--text)]">
                        Anywhere electricity flows.
                    </h2>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    {cases.map((c) => (
                        <div key={c.title} className="card panel-hover p-7 sm:p-9">
                            <div className="chip !text-[10.5px] mb-4">{c.tag}</div>
                            <div className="text-[22px] font-semibold text-[var(--text)]">{c.title}</div>
                            <p className="text-[15px] text-[var(--text-2)] leading-[1.6] mt-3">{c.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------------------------------------------------------------- ROADMAP */
function Roadmap() {
    const items = [
        { q: "Q4 2026", tag: "Live now",   t: "Early Bird Mining",        d: "Global launch of the Miner Terminal. First 100K participants lock in the genesis daily emission of 3M AONE and enjoy the maximum lifetime rate before the first halving." },
        { q: "Q1 2027", tag: "In build",   t: "Predictive-Failure AI v1", d: "Federated failure-detection models deployed to grid, utility and industrial partners. Miner nodes begin generating on-chain anomaly receipts settled on the Aegis L1." },
        { q: "Q2 2027", tag: "Committed",  t: "Tier-1 CEX Listings",      d: "AONE listed on major centralized exchanges. Public liquidity opens beyond the Auto-Pool and price discovery goes fully global." },
        { q: "Q3 2027", tag: "Committed",  t: "Enterprise Node Program",  d: "White-glove deployment for utilities, data centers, factories and renewables. Predictive-failure feed sold as a USDT subscription - direct revenue into the Auto-Pool." },
        { q: "Q4 2027", tag: "Committed",  t: "Cross-Chain Multisig Live", d: "Auto-Pool USDT reserves migrated to auditable 5-of-8 Gnosis Safes across Tron, BSC, Ethereum and Polygon. Fully on-chain treasury operations." },
        { q: "Q1 2028", tag: "Roadmap",    t: "Governance DAO",           d: "AONE holders take control of validator whitelisting, emission tuning and treasury allocations. Timelocked execution with quorum-based safeguards." },
        { q: "Q2 2028", tag: "Roadmap",    t: "Oracle Bridge",            d: "AONE/USDT rate anchored to a Chainlink + Pyth composite feed. Native chain oracles power lending, derivatives and RWA collateralization on Aegis L1." },
        { q: "Q4 2028", tag: "Roadmap",    t: "First Halving",            d: "Global daily emission halves from 3M to 1.5M AONE. Early miners retain locked-in rates. Scarcity kicks in and payout math tightens across the network." },
        { q: "Q2 2029", tag: "Roadmap",    t: "Institutional Custody",    d: "Aegis wallet certified for regulated custodians. B2B customers can hold AONE, mine, and settle predictive-failure invoices without leaving compliance-grade rails." },
        { q: "Q4 2029", tag: "Roadmap",    t: "Aegis Studio SDK",         d: "Public developer SDK for building on top of the predictive-failure API. Third-party dashboards, alerting tools and insurance underwriting go live." },
        { q: "Q2 2030", tag: "Roadmap",    t: "IPO Track",                d: "The Aegis One holdings company begins the pre-IPO process. AONE holders receive priority allocation via a token-gated tranche." },
        { q: "Q4 2030", tag: "Vision",     t: "Full DAO Handover",        d: "Foundation dissolves operational control. Auto-Pool, emission schedule, treasury multisigs and enterprise contracts run entirely under community governance." },
    ];
    return (
        <section className="py-24 px-5 sm:px-8 border-t" style={{ borderColor: "var(--border)" }} data-testid="home-roadmap">
            <div className="max-w-[1280px] mx-auto">
                <div className="mb-14 max-w-2xl">
                    <div className="eyebrow mb-4">Roadmap · Q4 2026 → Q4 2030</div>
                    <h2 className="text-[36px] sm:text-[52px] font-bold leading-[1.03] tracking-[-0.035em] text-[var(--text)]">
                        From Early Bird to autonomous DAO.
                    </h2>
                    <p className="mt-4 text-[15px] text-[var(--text-2)] leading-[1.6] max-w-xl">
                        Every milestone below is either live, committed with contracted delivery, or on the multi-year roadmap. No hype-cycle promises - each stage compounds real network revenue into the Auto-Pool that pays every miner.
                    </p>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((it) => (
                        <div key={it.t} className="card panel-hover p-6" data-testid={`roadmap-${it.q.replace(/\s+/g, "-").toLowerCase()}`}>
                            <div className="flex items-center justify-between">
                                <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-[var(--primary-glow)]">{it.q}</div>
                                <span className="chip !text-[10px] !py-0.5">{it.tag}</span>
                            </div>
                            <div className="text-[17px] font-semibold text-[var(--text)] mt-4">{it.t}</div>
                            <p className="text-[13.5px] text-[var(--text-2)] leading-[1.55] mt-2">{it.d}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------------------------------------------------------------- FINAL CTA */
function FinalCta({ onConnect }) {
    return (
        <section className="py-24 px-5 sm:px-8 border-t" style={{ borderColor: "var(--border)" }}>
            <div className="max-w-[1280px] mx-auto card p-10 sm:p-20 relative overflow-hidden text-center" style={{ background: "linear-gradient(135deg, var(--bg-surface) 0%, var(--bg-raised) 100%)" }}>
                <div className="relative z-10 max-w-3xl mx-auto">
                    <h2 className="text-[36px] sm:text-[64px] font-bold leading-[1.02] tracking-[-0.04em] text-[var(--text)]">
                        The grid never sleeps.<br />Neither do we.
                    </h2>
                    <p className="mt-6 text-[17px] leading-[1.55] text-[var(--text-2)] max-w-2xl mx-auto">
                        Join the DePIN network protecting the world&apos;s electrical infrastructure. No hardware. No ops. USDT in - AONE out.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-3 justify-center">
                        <button onClick={onConnect} data-testid="home-final-cta" className="btn-primary !text-[15px] !py-4 !px-7">
                            Start Mining Now <ArrowRight size={14} />
                        </button>
                        <Link to="/miner" className="btn-ghost !text-[15px] !py-4 !px-7">
                            View Plans
                        </Link>
                    </div>
                </div>
                <div className="absolute -right-32 -top-32 w-[500px] h-[500px] rounded-full opacity-40 pointer-events-none" style={{ background: "radial-gradient(circle, var(--primary-glow) 0%, transparent 65%)" }} />
                <div className="absolute -left-32 -bottom-32 w-[500px] h-[500px] rounded-full opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle, var(--primary) 0%, transparent 65%)" }} />
            </div>
        </section>
    );
}

/* ---------------------------------------------------------------- FOOTER */
function Footer() {
    return (
        <footer className="border-t py-16 px-5 sm:px-8" style={{ borderColor: "var(--border)" }}>
            <div className="max-w-[1280px] mx-auto">
                <div className="grid md:grid-cols-4 gap-10">
                    <div className="md:col-span-2">
                        <div className="flex items-center gap-3">
                            <AegisLogo size={30} />
                            <div>
                                <div className="text-[17px] font-semibold text-[var(--text)]">Aegis One</div>
                            </div>
                        </div>
                        <p className="text-[14px] text-[var(--text-3)] mt-5 max-w-md leading-[1.6]">
                            The decentralized network for electrical predictive failure intelligence. Community-owned, chain-secured, always on.
                        </p>
                        <div className="flex gap-3 mt-6">
                            <a
                                href="https://x.com/aegisonenetwork"
                                target="_blank"
                                rel="noreferrer"
                                data-testid="footer-x"
                                className="w-11 h-11 rounded-lg border border-[var(--border-strong)] flex items-center justify-center text-[var(--text-2)] hover:text-[var(--primary-glow)] hover:border-[var(--primary)] transition-colors"
                                aria-label="X (Twitter)"
                            >
                                <XLogoIcon size={16} weight="fill" />
                            </a>
                            <a
                                href="https://discord.gg/aegisone"
                                target="_blank"
                                rel="noreferrer"
                                data-testid="footer-discord"
                                className="w-11 h-11 rounded-lg border border-[var(--border-strong)] flex items-center justify-center text-[var(--text-2)] hover:text-[var(--primary-glow)] hover:border-[var(--primary)] transition-colors"
                                aria-label="Discord"
                            >
                                <DiscordLogoIcon size={16} weight="fill" />
                            </a>
                        </div>
                    </div>
                    <div>
                        <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)] mb-4">Product</div>
                        <div className="flex flex-col gap-2.5 text-[14px]">
                            <Link to="/miner" className="text-[var(--text-2)] hover:text-[var(--text)] transition-colors">Miner Terminal</Link>
                            <Link to="/wallet" className="text-[var(--text-2)] hover:text-[var(--text)] transition-colors">Wallet</Link>
                            <Link to="/swap" className="text-[var(--text-2)] hover:text-[var(--text)] transition-colors">Swap</Link>
                            <Link to="/referrals" className="text-[var(--text-2)] hover:text-[var(--text)] transition-colors">Referrals</Link>
                        </div>
                    </div>
                    <div>
                        <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)] mb-4">Network</div>
                        <div className="flex flex-col gap-2.5 text-[14px]">
                            <Link to="/validators" className="text-[var(--text-2)] hover:text-[var(--text)] transition-colors">Validators</Link>
                            <Link to="/tokenomics" className="text-[var(--text-2)] hover:text-[var(--text)] transition-colors">Tokenomics</Link>
                            <Link to="/wallet/withdraw" className="text-[var(--text-2)] hover:text-[var(--text)] transition-colors">Withdraw USDT</Link>
                        </div>
                    </div>
                </div>
                <div className="mt-14 pt-6 border-t flex flex-wrap justify-between gap-4 text-[11px] font-mono text-[var(--text-4)]" style={{ borderColor: "var(--border)" }}>
                    <div>© 2026 Aegis One Network - aegisonenetwork.com</div>
                    <div className="uppercase tracking-[0.14em]">L1 Blockchain</div>
                </div>
            </div>
        </footer>
    );
}
